/**
 * macro-releases — the pure half of the `macroReleases` section, "The releases,
 * grouped" (`docs/plans/macro.md`; mock `docs/mockups/macro-v2.html`, `GLECO`).
 *
 * Five groups — Growth · Inflation · Labor · Policy & rates · Sentiment — of
 * tiles. A tile is one series: its last reading, the change from the reading
 * before, an arrow, and the last 12 readings as columns.
 *
 * **The arrow is coloured by better or worse for the economy, not by up or
 * down.** Each series carries a direction (`dir`): +1 higher is better (GDP,
 * payrolls), −1 lower is better (CPI, claims), 0 neutral (the Fed's target,
 * hourly earnings), which is never coloured. The tone is read off the change
 * *as shown*, so a change that rounds to zero is "flat" and grey rather than a
 * green arrow over "+0.0".
 *
 * Every tile also says where its numbers come from today (`state`): `stored`
 * (in `macro_store` already), `massive` (the wave-1 Massive feeds,
 * branch `macro/macro-feeds`), `derive` (computed from two stored series) or
 * `pending` (a feed not built yet, so the tile draws no value — none today).
 * The FRED series (`us.fred.*`) are stored, and so are ISM and the Conference
 * Board: proprietary, on no free feed, so their history is the economic
 * calendar's released actuals (`us.calendar.*`), one point per release dated
 * the release day.
 *
 * No React, no fetch: the data arrives through `api/macro-releases.ts`, mapped
 * from the macro store's series by `lib/macro-release-series.ts`.
 */

export const RELEASE_GROUPS = ["Growth", "Inflation", "Labor", "Policy & rates", "Sentiment"] as const;
export type ReleaseGroup = (typeof RELEASE_GROUPS)[number];

/** Where a tile's numbers come from today — the data-state badge. */
export type ReleaseState = "stored" | "massive" | "derive" | "pending";

/**
 * How a value and its change are written.
 *
 * | unit | value | change |
 * |---|---|---|
 * | `pct` | `2.9%` | `+0.1` (points) |
 * | `rate` | `2.31%` | `+4bp` |
 * | `share` | `24%` | `−3pp` |
 * | `index` | `49.8` | `+0.7` |
 * | `thousands` | `135K` | `−7K` |
 * | `millions` | `7.21M` | `−0.10` |
 * | `range` | `3.75–4.00%` | `held` / `+25bp` (the upper bound) |
 */
export type ReleaseUnit = "pct" | "rate" | "share" | "index" | "thousands" | "millions" | "range";

/** +1 higher is better for the economy, −1 lower is better, 0 neutral. */
export type ReleaseDir = 1 | -1 | 0;

export interface ReleaseSpec {
  /** Stable key, also the key in {@link ReleaseData}. */
  id: string;
  group: ReleaseGroup;
  label: string;
  unit: ReleaseUnit;
  dir: ReleaseDir;
  state: ReleaseState;
  /** Where the series is, for the tile's tooltip — a `macro_store` id or the vendor. */
  from: string;
}

/** One reading, already in the tile's display unit. `low` is a range's lower bound. */
export interface ReleasePoint {
  date: string;
  value: number;
  low?: number;
}

/** Readings per tile id, oldest first. A missing id is "no data". */
export type ReleaseData = Partial<Record<string, readonly ReleasePoint[]>>;

/** The tiles, in the mock's order. */
export const RELEASE_SPECS: readonly ReleaseSpec[] = [
  { id: "gdp", group: "Growth", label: "Real GDP QoQ ann.", unit: "pct", dir: 1, state: "stored", from: "us.fred.a191rl1q225sbea" },
  { id: "retail", group: "Growth", label: "Retail Sales MoM", unit: "pct", dir: 1, state: "stored", from: "us.fred.rsafs, month over month" },
  { id: "indpro", group: "Growth", label: "Industrial Prod. YoY", unit: "pct", dir: 1, state: "stored", from: "us.fred.indpro, year over year" },
  { id: "ism", group: "Growth", label: "ISM Manufacturing", unit: "index", dir: 1, state: "stored", from: "us.calendar.ism_manufacturing, each release" },

  { id: "cpi", group: "Inflation", label: "CPI YoY", unit: "pct", dir: -1, state: "stored", from: "us.inflation.cpi, year over year" },
  { id: "corePce", group: "Inflation", label: "Core PCE YoY", unit: "pct", dir: -1, state: "stored", from: "us.inflation.pce_core, year over year" },
  { id: "ppi", group: "Inflation", label: "PPI MoM", unit: "pct", dir: -1, state: "stored", from: "us.fred.ppifis, month over month" },
  { id: "breakeven5y", group: "Inflation", label: "5Y breakeven", unit: "rate", dir: -1, state: "stored", from: "us.expectations.market_5_year" },

  { id: "unemployment", group: "Labor", label: "Unemployment", unit: "pct", dir: -1, state: "massive", from: "us.labor.unemployment_rate" },
  { id: "payrolls", group: "Labor", label: "Nonfarm Payrolls", unit: "thousands", dir: 1, state: "stored", from: "us.fred.payems, monthly change" },
  { id: "claims", group: "Labor", label: "Jobless Claims", unit: "thousands", dir: -1, state: "stored", from: "us.fred.icsa, weekly" },
  { id: "jolts", group: "Labor", label: "Job openings (JOLTS)", unit: "millions", dir: 1, state: "massive", from: "us.labor.job_openings" },
  { id: "ahe", group: "Labor", label: "Avg hourly earnings YoY", unit: "pct", dir: 0, state: "massive", from: "us.labor.avg_hourly_earnings, year over year" },

  { id: "fedFunds", group: "Policy & rates", label: "Fed Funds (target)", unit: "range", dir: 0, state: "massive", from: "us.funding.fed_funds_target_lower / _upper" },
  { id: "realYield10y", group: "Policy & rates", label: "10Y real yield", unit: "rate", dir: 0, state: "derive", from: "us.treasury.yield_10_year − us.expectations.market_10_year" },
  { id: "mortgage30y", group: "Policy & rates", label: "30Y Mortgage", unit: "rate", dir: -1, state: "stored", from: "us.fred.mortgage30us, weekly" },

  { id: "umich", group: "Sentiment", label: "UMich Sentiment", unit: "index", dir: 1, state: "stored", from: "us.fred.umcsent" },
  { id: "confBoard", group: "Sentiment", label: "Conf. Board Confidence", unit: "index", dir: 1, state: "stored", from: "us.calendar.cb_consumer_confidence, each release" },
  { id: "recession", group: "Sentiment", label: "Recession prob.", unit: "share", dir: -1, state: "stored", from: "us.fred.recprousm156n" },
];

/** The specs of one group, in order. */
export function groupSpecs(group: ReleaseGroup, specs: readonly ReleaseSpec[] = RELEASE_SPECS): ReleaseSpec[] {
  return specs.filter((s) => s.group === group);
}

/** How many readings a tile's column chart holds. */
export const COLUMN_COUNT = 12;

const MINUS = "−";

const signed = (n: number, text: string): string => (n > 0 ? `+${text}` : n < 0 ? `${MINUS}${text}` : text);

/** The value as the tile's big number. */
export function formatValue(unit: ReleaseUnit, p: ReleasePoint): string {
  switch (unit) {
    case "pct":
      return `${p.value.toFixed(1)}%`;
    case "rate":
      return `${p.value.toFixed(2)}%`;
    case "share":
      return `${p.value.toFixed(0)}%`;
    case "index":
      return p.value.toFixed(1);
    case "thousands":
      return `${p.value.toFixed(0)}K`;
    case "millions":
      return `${p.value.toFixed(2)}M`;
    case "range":
      return p.low === undefined
        ? `${p.value.toFixed(2)}%`
        : `${p.low.toFixed(2)}–${p.value.toFixed(2)}%`;
  }
}

/** −1, 0 or +1: the sign of the change after rounding to what is shown. */
export type ChangeSign = -1 | 0 | 1;

export interface ReleaseChange {
  sign: ChangeSign;
  /** `+0.1`, `−7K`, `+4bp`, `flat`, `held`. */
  text: string;
}

/** Decimal places each unit's big number is written to. */
const SHOWN_DP: Record<ReleaseUnit, number> = {
  pct: 1,
  index: 1,
  millions: 2,
  rate: 2,
  range: 2,
  share: 0,
  thousands: 0,
};

/**
 * The value exactly as {@link formatValue} writes it, in steps of its last digit
 * (`2.94` as `pct` → 29). Read back off `toFixed`, the same call that writes the
 * number, so the change can never be computed from a rounding the tile does not show.
 */
function shownSteps(unit: ReleaseUnit, v: number): number {
  const dp = SHOWN_DP[unit];
  return Math.round(Number(v.toFixed(dp)) * 10 ** dp);
}

/**
 * The change from `prev` to `last`, **taken between the two values as shown**, so
 * the big number and the change always agree: `2.9%` after `2.9%` is flat even
 * when the readings were 2.86 and 2.94, and `3.0%` after `2.8%` is `+0.2` even
 * when the raw difference (0.12) would round to 0.1. A `range` moves on its upper
 * bound.
 */
export function formatChange(unit: ReleaseUnit, last: ReleasePoint, prev: ReleasePoint): ReleaseChange {
  const q = shownSteps(unit, last.value) - shownSteps(unit, prev.value);
  const sign: ChangeSign = q > 0 ? 1 : q < 0 ? -1 : 0;
  if (sign === 0) return { sign, text: unit === "range" ? "held" : "flat" };
  const a = Math.abs(q);
  let text: string;
  switch (unit) {
    case "pct":
    case "index":
      text = (a / 10).toFixed(1);
      break;
    case "millions":
      text = (a / 100).toFixed(2);
      break;
    case "rate":
    case "range":
      // Steps of 0.01 percentage points are basis points.
      text = `${a}bp`;
      break;
    case "share":
      text = `${a}pp`;
      break;
    case "thousands":
      text = `${a}K`;
      break;
  }
  return { sign, text: signed(sign, text) };
}

/** What the arrow's colour says. */
export type ReleaseTone = "better" | "worse" | "neutral";

/** Better or worse for the economy: the change's sign times the series' direction. */
export function releaseTone(sign: ChangeSign, dir: ReleaseDir): ReleaseTone {
  const t = sign * dir;
  return t > 0 ? "better" : t < 0 ? "worse" : "neutral";
}

/** ▲ ▼ for a move, ● for none. */
export function releaseArrow(sign: ChangeSign): string {
  return sign > 0 ? "▲" : sign < 0 ? "▼" : "●";
}

/** One column of the last-12 chart, in a `width × height` viewBox. */
export interface ReleaseColumn {
  x: number;
  y: number;
  w: number;
  h: number;
  /** The latest reading is drawn solid, a negative one in the negative colour. */
  kind: "last" | "neg" | "pos";
  point: ReleasePoint;
}

/**
 * The columns for `points` (already the last {@link COLUMN_COUNT}), rising from
 * zero — the mock's geometry: 2px of headroom, zero included in the scale, so
 * a series that crosses zero hangs its negatives below the line.
 */
export function releaseColumns(points: readonly ReleasePoint[], width = 160, height = 36): ReleaseColumn[] {
  if (points.length === 0) return [];
  const vals = points.map((p) => p.value);
  const lo = Math.min(0, ...vals);
  const hi = Math.max(0, ...vals);
  const span = hi - lo || 1;
  const top = 2;
  const plot = height - 2 * top;
  const Y = (v: number) => height - top - ((v - lo) / span) * plot;
  const bw = width / points.length;
  const pad = Math.min(1.5, bw / 4);
  const y0 = Y(0);
  return points.map((p, i) => {
    const y = Y(p.value);
    return {
      x: i * bw + pad,
      y: Math.min(y, y0),
      w: Math.max(0.5, bw - 2 * pad),
      h: Math.max(1, Math.abs(y - y0)),
      kind: i === points.length - 1 ? "last" : p.value < 0 ? "neg" : "pos",
      point: p,
    };
  });
}

/** Everything one tile draws. */
export interface ReleaseTile {
  spec: ReleaseSpec;
  /**
   * `ready` has a value; `pending` is a feed not built yet; `empty` is a built
   * feed that returned nothing (not ingested, or the route refused).
   */
  status: "ready" | "pending" | "empty";
  value?: string;
  change?: ReleaseChange;
  tone?: ReleaseTone;
  /** The latest reading's date. */
  asOf?: string;
  columns: ReleaseColumn[];
}

/** One tile from its spec and its readings (oldest first). */
export function releaseTile(spec: ReleaseSpec, points: readonly ReleasePoint[] | undefined): ReleaseTile {
  if (spec.state === "pending") return { spec, status: "pending", columns: [] };
  if (!points || points.length === 0) return { spec, status: "empty", columns: [] };
  const last12 = points.slice(-COLUMN_COUNT);
  const last = last12[last12.length - 1]!;
  const prev = points.length >= 2 ? points[points.length - 2] : undefined;
  const change = prev ? formatChange(spec.unit, last, prev) : undefined;
  return {
    spec,
    status: "ready",
    value: formatValue(spec.unit, last),
    change,
    tone: change ? releaseTone(change.sign, spec.dir) : undefined,
    asOf: last.date,
    columns: releaseColumns(last12),
  };
}

/** The five groups, each with its tiles. */
export function releaseGroups(
  data: ReleaseData,
  specs: readonly ReleaseSpec[] = RELEASE_SPECS,
): { group: ReleaseGroup; tiles: ReleaseTile[] }[] {
  return RELEASE_GROUPS.map((group) => ({
    group,
    tiles: groupSpecs(group, specs).map((s) => releaseTile(s, data[s.id])),
  })).filter((g) => g.tiles.length > 0);
}
